import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Modal, IconButton, Backdrop } from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BreakpointUtils from '../utils/BreakpointUtils';

export interface TeaserFigureModalProps {
    imageUrl: string;
    figureText?: string;
    open: boolean;
    onClose: () => void;
    toPrev: () => void;
    toNext: () => void;
    singleImageMode: boolean;
}

export default function TeaserFigureModal(props: TeaserFigureModalProps) {
    const SPACE_FOR_BUTTONS = 200; // 2*(60px + 24px + 16px) = 200px
    const onMobile = BreakpointUtils.lessThanMedium();
    const aspectRatio = useRef(1);
    const [screenAspectRatio, setScreenAspectRatio] = useState(1);
    const [imageWidth, setImageWidth] = useState('');
    const [imageHeight, setImageHeight] = useState('');
    const [imageLoaded, setImageLoaded] = useState(false);

    useEffect(() => {
        const updateScreenAspectRatio = () => {
            setScreenAspectRatio(window.innerWidth / window.innerHeight);
        };
        updateScreenAspectRatio();
        window.addEventListener('resize', updateScreenAspectRatio);
        return () => {
            window.removeEventListener('resize', updateScreenAspectRatio);
        };
    }, []);

    // update the aspect ratio when the image changes
    useEffect(() => {

        const img = new Image();
        img.src = props.imageUrl;
        img.onload = () => {
            aspectRatio.current = img.naturalWidth / img.naturalHeight;
            if (aspectRatio.current > screenAspectRatio) {
                // image is wider than screen so image width is the limiting factor
                const width = window.innerWidth - SPACE_FOR_BUTTONS;
                setImageWidth(width.toString() + 'px');
                setImageHeight('auto');
            } else {
                // image is taller than screen so image height is the limiting factor
                setImageWidth('auto');
                setImageHeight('90vh');
            }
            setImageLoaded(true);
        };
    }, [props.imageUrl, screenAspectRatio]);

    const handleClose = () => {
        props.onClose();
    };

    // no modal on mobiles (there is not enough space for the text and the buttons)
    if (onMobile) {
        return null;
    }

    return (
        <Modal
            open={props.open}
            onClose={handleClose}
            slots={{ backdrop: Backdrop }}
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    },
                },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '100%',
                    height: '100%',
                }}
                onClick={handleClose}
            >
                <IconButton
                    disabled={props.singleImageMode}
                    sx={{
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        color: 'white',
                        width: '60px',
                        height: '60px',
                        borderRadius: '10px',
                        marginRight: 2,
                        marginLeft: 3,
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        },
                    }}
                    onClick={(event) => {
                        event.stopPropagation();
                        setImageLoaded(false);
                        props.toPrev();
                    }}
                >
                    <ArrowBackIcon />
                </IconButton>
                {/* Box Containing the Image and the Caption */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: window.innerWidth - SPACE_FOR_BUTTONS,
                        height: '95vh',
                        maxWidth: '1536px',
                    }}
                    onClick={handleClose}
                >
                    {imageLoaded && (
                        <img
                            src={props.imageUrl}
                            alt="Teaser Figure"
                            style={{
                                borderRadius: '10px',
                                padding: '10px',
                                backgroundColor: 'white',
                                width: imageWidth,
                                height: imageHeight,
                                maxWidth: '1536px',
                                maxHeight: '80vh'
                            }}
                        />
                    )}
                    <Box
                        sx={{
                            mt: 2,
                            textAlign: 'center',
                            overflowY: 'scroll',
                            color: 'white',
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            padding: '10px',
                            borderRadius: '10px',
                        }}
                    >
                        <Typography variant="body2" sx={{ fontSize: '12.5pt' }}>{props.figureText}</Typography>
                    </Box>
                </Box>
                <IconButton
                    disabled={props.singleImageMode}
                    sx={{
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        color: 'white',
                        width: '60px',
                        height: '60px',
                        borderRadius: '10px',
                        marginLeft: 2,
                        marginRight: 3,
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        },
                    }}
                    onClick={(event) => {
                        event.stopPropagation();
                        setImageLoaded(false);
                        props.toNext();
                    }}
                >
                    <ArrowForwardIcon />
                </IconButton>
            </Box>
        </Modal>
    );
}